// SEO landing page configs. Each drives one route with unique H1, meta, copy, and FAQ.
// All CTAs share the Calendly link. Add real client results to `proofPoints` when you have them.

const areaServed = ['US', 'CA', 'GB', 'IN'];

const orgRef = { '@id': 'https://www.firstpageconsultants.com/#organization' };

const makeServiceLd = (name, description) => ({
  '@type': 'Service',
  name,
  description,
  provider: orgRef,
  areaServed,
});

const makeFaqLd = (faqs) => ({
  '@type': 'FAQPage',
  mainEntity: faqs.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
});

const makeBreadcrumbLd = (title, path) => ({
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.firstpageconsultants.com/' },
    { '@type': 'ListItem', position: 2, name: title, item: `https://www.firstpageconsultants.com${path}` },
  ],
});

const graph = (title, path, description, extraNodes = []) => ({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': `https://www.firstpageconsultants.com${path}#webpage`,
      url: `https://www.firstpageconsultants.com${path}`,
      name: title,
      description,
      isPartOf: { '@id': 'https://www.firstpageconsultants.com/#website' },
      about: orgRef,
      inLanguage: 'en-US',
    },
    makeBreadcrumbLd(title, path),
    ...extraNodes,
  ],
});

export const LANDING_PAGES = {
  amazonPpcAgency: {
    path: '/amazon-ppc-agency',
    seo: {
      title: 'Amazon PPC Agency | Full-Service Ads Management — First Page Consultants',
      description:
        'Amazon PPC agency built by operators. Full-service Sponsored Products, Brands and Display management. $500k+ in monthly ad spend. Book a free strategy call.',
      keywords:
        'amazon ppc agency, amazon ads agency, amazon ppc management, sponsored products management, sponsored brands agency, amazon advertising agency',
      hreflang: [
        { lang: 'en-US', href: '/amazon-ppc-agency' },
        { lang: 'en-CA', href: '/amazon-ppc-agency' },
        { lang: 'x-default', href: '/amazon-ppc-agency' },
      ],
    },
    hero: {
      eyebrow: 'Amazon PPC Agency',
      h1: 'Amazon PPC agency built by operators who ran seven-figure brands',
      sub: 'Full-service Amazon PPC management for growth-focused brands. Senior operators. Daily optimization. Margin discipline over vanity ROAS. Book a free strategy call.',
      trust: 'No junior handoffs. Direct access to senior Amazon operators from day one.',
    },
    bodyHeadline: 'What full-service Amazon PPC management should actually look like',
    bodyIntro:
      'Most brands do not need more spend. They need a campaign structure that reflects real demand, disciplined bid management, and reporting that drives decisions. Here is how we run Amazon PPC across Sponsored Products, Sponsored Brands, and Sponsored Display.',
    bodyPoints: [
      {
        h: 'Demand-first keyword and campaign architecture',
        p: 'We rebuild your campaigns around real search demand, not guesswork. Search-term isolation, single-keyword ad groups where they matter, and a clear split between exact, phrase, and broad.',
      },
      {
        h: 'Daily bid, budget, and negative management',
        p: 'Bids move daily based on placement performance, dayparting, and margin. Wasted spend gets sniped with negatives every week. No auto-set-and-forget rules that quietly bleed budget.',
      },
      {
        h: 'Sponsored Brands and Display, done right',
        p: 'Video ads, category-level Sponsored Brands, and product-defence Sponsored Display campaigns — deployed to defend hero SKUs and steal share from ranked competitors.',
      },
      {
        h: 'Weekly reporting that answers "what next?"',
        p: 'You get a plain-English weekly report: what changed, why it changed, and the two decisions we made this week. No 30-tab dashboards you never open.',
      },
      {
        h: 'A single owner, senior-led',
        p: 'One senior operator owns your account end to end. No task-team handoffs. No account manager who just relays messages between you and the person doing the work.',
      },
    ],
    faqs: [
      {
        q: 'What does an Amazon PPC agency actually do day to day?',
        a: 'Daily keyword and bid management across Sponsored Products, Sponsored Brands, and Sponsored Display; weekly negative-keyword mining; ongoing search-term isolation; campaign restructures when performance data warrants it; and weekly reporting that connects spend to profit, not just ACOS.',
      },
      {
        q: 'How much does an Amazon PPC agency cost?',
        a: 'Most Amazon PPC agencies charge either a percentage of ad spend (5–15%) or a fixed monthly retainer. Our PPC Management retainer is $799/month with no upsells and no hidden performance fees.',
      },
      {
        q: 'How long before I see results from a new PPC agency?',
        a: 'Weeks 1–2 are foundation work (audit, negatives, campaign hygiene, bid recalibration). Meaningful ACOS and TACOS improvement usually shows in weeks 3–6. Scale phase typically kicks in from week 8 onwards.',
      },
      {
        q: 'Do you work with brands outside the US?',
        a: 'Yes. We run Amazon PPC across US (amazon.com), Canada (amazon.ca), UK (amazon.co.uk), and India (amazon.in) marketplaces.',
      },
      {
        q: 'What tools do you use to manage Amazon PPC?',
        a: 'Helium 10 for keyword research and listing diagnostics, Data Dive for demand analysis, and Adbrew for rule-based bid optimization and dayparting. Tools execute faster; experience decides better.',
      },
    ],
    get jsonLd() {
      return graph(this.seo.title, this.path, this.seo.description, [
        makeServiceLd(
          'Amazon PPC Management',
          'Full-service Amazon PPC agency management: Sponsored Products, Sponsored Brands, and Sponsored Display. Daily optimization and disciplined bid control.'
        ),
        makeFaqLd(this.faqs),
      ]);
    },
  },

  amazonAdsAgencyUsa: {
    path: '/amazon-ads-agency-usa',
    seo: {
      title: 'Amazon Ads Agency USA | Senior-Led PPC for US Brands — First Page Consultants',
      description:
        'Amazon Ads agency for US brands scaling on amazon.com. Senior operators, daily optimization, no junior handoffs. Book a free strategy call today.',
      keywords:
        'amazon ads agency USA, amazon advertising agency United States, amazon ppc agency USA, amazon.com ads agency, best amazon agency US',
      hreflang: [
        { lang: 'en-US', href: '/amazon-ads-agency-usa' },
        { lang: 'x-default', href: '/amazon-ads-agency-usa' },
      ],
    },
    hero: {
      eyebrow: 'Amazon Ads Agency · USA',
      h1: 'Amazon Ads agency for US brands who take amazon.com seriously',
      sub: 'Senior-led Amazon Ads and Seller Central management for US-based brands. Built for owners who want accountability, margin discipline, and clear ownership.',
      trust: 'US timezone coverage. Senior operators. Weekly reporting in plain English.',
    },
    bodyHeadline: 'Built for the amazon.com marketplace',
    bodyIntro:
      "The amazon.com marketplace is the largest, most competitive Amazon storefront in the world. Running ads here means competing against sophisticated aggregators, category leaders, and 250,000+ active third-party sellers. Here is how we compete and win on amazon.com.",
    bodyPoints: [
      {
        h: 'US-timezone communication and reporting',
        p: 'Weekly reports land in your inbox on Monday mornings — US Eastern. Emergency asks get answered same-day, not next-day.',
      },
      {
        h: 'Brand Registry, Amazon Vine, and A+ Content',
        p: 'We handle the amazon.com brand-side toolkit end to end — Brand Registry setup, Amazon Vine enrollment for new launches, and A+ / Premium A+ content that supports paid traffic.',
      },
      {
        h: 'FBA-aware campaign strategy',
        p: 'Bid strategy and budget pacing that respect FBA fee cycles, storage-limit dynamics, and inbound receiving delays. So you never scale spend right into a stock-out.',
      },
      {
        h: 'Prime Day and Q4 planning',
        p: 'Lead-up campaigns for Prime Day, Prime Big Deal Days, Black Friday, and Cyber Monday planned 60 days ahead — with pre-event traffic ramps, in-event budget uncapping, and post-event ranking defence.',
      },
      {
        h: 'DTC-to-Amazon and Amazon-to-DTC growth loops',
        p: 'For brands running both DTC and Amazon, we help design the growth loop between the two — using Amazon for discovery and DTC for LTV, or the reverse.',
      },
    ],
    faqs: [
      {
        q: 'Do you work with US-based Amazon brands only?',
        a: 'We work primarily with US and Canada based brands, plus select UK and India-based brands. Our sweet spot is US-headquartered brands doing $1M–$50M in annual Amazon revenue.',
      },
      {
        q: 'Do you have US timezone coverage?',
        a: 'Yes. Weekly reports go out Monday morning US Eastern. Same-day response for urgent asks during US business hours.',
      },
      {
        q: 'Can you help with Prime Day and Q4 planning?',
        a: 'Yes — we plan Prime Day, Prime Big Deal Days, and Q4 sales events 60 days in advance, including pre-event traffic ramps, in-event budget uncapping, and post-event ranking defence.',
      },
      {
        q: 'Do you work with Amazon aggregators?',
        a: 'Yes. We have supported portfolios owned by Amazon aggregators as well as VC-backed and PE-backed brand groups.',
      },
    ],
    get jsonLd() {
      return graph(this.seo.title, this.path, this.seo.description, [
        makeServiceLd(
          'Amazon Ads Agency for US Brands',
          'Senior-led Amazon Ads and account management for US-based brands scaling on amazon.com.'
        ),
        makeFaqLd(this.faqs),
      ]);
    },
  },

  amazonAdsAgencyCanada: {
    path: '/amazon-ads-agency-canada',
    seo: {
      title: 'Amazon Ads Agency Canada | Amazon.ca PPC Management — First Page Consultants',
      description:
        'Amazon Ads agency for Canadian brands scaling on amazon.ca and amazon.com. Senior-led Amazon PPC and account management. Book a free strategy call.',
      keywords:
        'amazon ads agency Canada, amazon.ca ads agency, amazon ppc Canada, amazon consulting Canada, amazon advertising Canada',
      hreflang: [
        { lang: 'en-CA', href: '/amazon-ads-agency-canada' },
        { lang: 'x-default', href: '/amazon-ads-agency-canada' },
      ],
    },
    hero: {
      eyebrow: 'Amazon Ads Agency · Canada',
      h1: 'Amazon Ads agency for Canadian brands scaling on amazon.ca and amazon.com',
      sub: 'Senior-led Amazon PPC and account management built for Canadian sellers — whether you are winning locally on amazon.ca, expanding into amazon.com, or running both.',
      trust: 'Bilingual English support. Familiar with amazon.ca and amazon.com marketplaces.',
    },
    bodyHeadline: 'Amazon.ca and cross-border scale, done right',
    bodyIntro:
      'Canadian Amazon brands face a specific challenge: the amazon.ca marketplace is smaller, less competitive, and rewards patient PPC — but the real growth ceiling is unlocked only when you expand into amazon.com. Here is how we help Canadian brands navigate both.',
    bodyPoints: [
      {
        h: 'Amazon.ca as your foundation',
        p: 'We build a clean amazon.ca campaign structure, use CAD conversion economics honestly (not USD-first math), and defend hero SKUs before expanding budgets.',
      },
      {
        h: 'North America expansion strategy',
        p: 'Cross-border FBA planning, US brand-registry setup, US-side listing SEO, and US-competitive bid strategy — so amazon.com becomes a real growth channel, not a spend leak.',
      },
      {
        h: 'CAD vs USD margin discipline',
        p: 'We report profit in CAD, but plan spend using true SKU-level margin, factoring FX, US FBA fees, and cross-border logistics. No surprises at the end of the quarter.',
      },
      {
        h: 'Sponsored Brands and DSP for smaller marketplaces',
        p: 'On amazon.ca specifically, Sponsored Brands and Sponsored Display often out-perform Sponsored Products for defence. We use them aggressively.',
      },
      {
        h: 'Weekly reporting in plain English',
        p: 'One senior operator, weekly reports, and a real conversation — not a dashboard link.',
      },
    ],
    faqs: [
      {
        q: 'Do you work with Canadian Amazon sellers?',
        a: 'Yes. Canadian brands are one of our core markets. We run Amazon PPC and account management on both amazon.ca and amazon.com for Canadian sellers.',
      },
      {
        q: 'Can you help me expand from amazon.ca to amazon.com?',
        a: 'Yes — expansion strategy is a common ask. This includes US Brand Registry, US-side listing SEO, cross-border FBA planning, and a US-competitive PPC launch strategy.',
      },
      {
        q: 'Do you report in CAD or USD?',
        a: 'Whichever the brand prefers. Most Canadian clients want reporting in CAD for the amazon.ca marketplace and USD for amazon.com — we handle both cleanly.',
      },
      {
        q: 'What is the minimum spend on amazon.ca to make ads work?',
        a: 'On amazon.ca, we recommend a minimum of ~$3,000 CAD/month in ad spend to run a disciplined multi-campaign structure. Below that, the account is usually not ready for a full-service agency and would be better served by strategic advisory.',
      },
    ],
    get jsonLd() {
      return graph(this.seo.title, this.path, this.seo.description, [
        makeServiceLd(
          'Amazon Ads Agency for Canadian Brands',
          'Senior-led Amazon PPC and account management for Canadian brands scaling on amazon.ca and expanding into amazon.com.'
        ),
        makeFaqLd(this.faqs),
      ]);
    },
  },

  bestAmazonPpcAgency: {
    path: '/best-amazon-ppc-agency',
    seo: {
      title: 'How to Choose the Best Amazon PPC Agency in 2026 — First Page Consultants',
      description:
        'A practical guide to evaluating the best Amazon PPC agency for your brand. What senior-led operators do differently, and how to tell before you sign.',
      keywords:
        'best amazon ppc agency, top amazon ads agency, best amazon advertising agency, best amazon ppc management, choosing amazon agency',
      hreflang: [
        { lang: 'en-US', href: '/best-amazon-ppc-agency' },
        { lang: 'en-CA', href: '/best-amazon-ppc-agency' },
        { lang: 'x-default', href: '/best-amazon-ppc-agency' },
      ],
    },
    hero: {
      eyebrow: 'Choosing an Agency',
      h1: 'How to evaluate the best Amazon PPC agency for your brand',
      sub: 'Most "best amazon ppc agency" lists are pay-to-play. Here is a practical, unbiased framework to evaluate an Amazon PPC agency before you sign — plus a straight answer on what we do differently.',
      trust: 'Written by operators who have hired, been hired by, and been fired by other Amazon PPC agencies.',
    },
    bodyHeadline: '6 questions to ask before you hire an Amazon PPC agency',
    bodyIntro:
      'Every Amazon PPC agency claims senior expertise, daily optimization, and profitable scale. Very few can defend those claims in the sales call. These six questions separate an agency that will actually move your business from one that will quietly bleed spend.',
    bodyPoints: [
      {
        h: '1. Who exactly is running my account, and how many other accounts do they run?',
        p: 'The single most predictive question. A senior operator running 3 accounts will beat a "senior strategist" whose 8 junior account managers each run 12 accounts.',
      },
      {
        h: '2. Show me a real weekly report from another client.',
        p: 'If the report is a dashboard screenshot, run. If it is a written narrative — "here is what changed, why, and the two decisions we made" — that is an operator report.',
      },
      {
        h: '3. What is your negative-keyword mining cadence?',
        p: 'The answer should be a specific weekly ritual with a search-term-report threshold. "We check regularly" is a red flag.',
      },
      {
        h: '4. Do you get paid on ad spend, revenue, or a fixed retainer?',
        p: 'Ad-spend-based fees create the wrong incentive. Fixed retainers align the agency with margin, not spend.',
      },
      {
        h: '5. How do you handle a listing that has an ad problem but a conversion-rate problem underneath?',
        p: 'The right answer is: they fix conversion first. Wrong answer: they turn ads down and blame the listing.',
      },
      {
        h: '6. Can I talk to two current clients before we sign?',
        p: 'Any agency serious about their work will make that happen. Silence is the answer.',
      },
    ],
    faqs: [
      {
        q: 'How do I know if an Amazon PPC agency is actually good?',
        a: 'Ask who is running the account day to day, request a real weekly report, verify the negative-keyword cadence, understand the fee model, and speak to two current clients. If any of those five questions gets a soft answer, keep looking.',
      },
      {
        q: 'Are the "best amazon ppc agency" lists on Google reliable?',
        a: 'Mostly no. Most of those lists are pay-to-play or affiliate-driven. The best signal is always a direct conversation with an operator plus 1–2 unfiltered client references.',
      },
      {
        q: 'What separates First Page Consultants from other Amazon PPC agencies?',
        a: 'Three things: one senior operator owns your account end to end, we charge a fixed retainer so we are aligned with margin not spend, and we work with a limited number of brands so the work stays hands-on.',
      },
      {
        q: 'Do you offer a free audit?',
        a: 'Yes — the first strategy call includes a directional audit of your current account, plus a clear read on whether we are the right fit. There is no obligation.',
      },
    ],
    get jsonLd() {
      return graph(this.seo.title, this.path, this.seo.description, [
        makeServiceLd(
          'Amazon PPC Agency Evaluation and Advisory',
          'A framework for evaluating and choosing the best Amazon PPC agency for your brand.'
        ),
        makeFaqLd(this.faqs),
      ]);
    },
  },

  amazonPpcAgencyNorthAmerica: {
    path: '/amazon-ppc-agency-north-america',
    seo: {
      title: 'Amazon PPC Agency for North American Brands | Fair Pricing — First Page Consultants',
      description:
        'Senior-led Amazon PPC agency for US and Canada brands. Same tech stack as $3,000+/mo North American agencies — at a flat $799 retainer, no percentage-of-spend fees. Book a free strategy call.',
      keywords:
        'amazon ppc agency north america, amazon ads agency north america, affordable amazon ppc agency, amazon agency pricing, amazon ppc agency cost, amazon agency fees, amazon ads retainer',
      hreflang: [
        { lang: 'en-US', href: '/amazon-ppc-agency-north-america' },
        { lang: 'en-CA', href: '/amazon-ppc-agency-north-america' },
        { lang: 'x-default', href: '/amazon-ppc-agency-north-america' },
      ],
    },
    hero: {
      eyebrow: 'Priced for Profitable North American Brands',
      h1: 'The Amazon PPC agency built for North American brands who outgrew DIY — without the $3,000+ retainer',
      sub: 'Same tech stack. Same senior-operator experience. Flat $799/month retainer, no percentage-of-spend games. We work with a limited number of US and Canada based brands who care about margin as much as growth.',
      trust: 'Not the cheapest Amazon PPC agency. Not trying to be. Built for brands where ROAS discipline matters more than fee shopping.',
    },
    bodyHeadline: "Why top North American Amazon agencies charge $3,000+ a month — and why we don't",
    bodyIntro:
      'Walk into a conversation with any top-tier North American Amazon agency and the quote is roughly the same: $3,000–$7,500/month base retainer, plus 10–15% of ad spend, plus setup fees. The actual deliverables are not dramatically different from what we do. The pricing math is. Here is what you are paying for at a $3,000+ agency — and what we built instead.',
    bodyPoints: [
      {
        h: 'The $3,000+ agency model: big team, big overhead, 20+ brands per operator',
        p: 'The large North American Amazon agencies run on hiring velocity: 50–200 person teams, downtown offices, sales staff, ops layers, and account managers who often juggle 20+ brands at once. The cost of all that staff gets passed to you — plus a percentage of your ad spend, which quietly aligns the agency with spending more, not spending better.',
      },
      {
        h: 'Our model: smaller team, same tech stack, no spend-based fees',
        p: 'Helium 10, Data Dive, Adbrew — the exact same tools the $3,000/mo agencies use. We just do not pass on the overhead of a 50-person team you will never meet. $799/month, flat. No percentage of spend. No setup fees. No upsells.',
      },
      {
        h: 'Capped intake — not an infinite-scale agency',
        p: 'We cap at around 15 active brands at a time. That is a deliberate ceiling: it is the number where one senior operator can run every account hands-on, without junior handoffs. When we are full, we close the waitlist.',
      },
      {
        h: 'Truly North American focused',
        p: 'US timezone coverage, native familiarity with amazon.com and amazon.ca marketplaces, Prime Day / Black Friday / Cyber Monday calendar discipline, Amazon Vine enrolment on new launches, and A+ / Premium A+ content planning. We work almost exclusively with US and Canada based brands.',
      },
      {
        h: 'Not for everyone — and we are honest about that',
        p: 'If you are sub-$30k/month in Amazon revenue, we are too expensive for the stage. If you want a $200/month task-team doing "optimizations", we are not that either. We work best with brands doing $50k–$2M/month on Amazon where $799 is a rounding error and real ROAS discipline moves the P&L.',
      },
    ],
    faqs: [
      {
        q: 'How can you charge $799/month when other North American Amazon PPC agencies charge $3,000+?',
        a: 'Lower overhead. We run a small, senior-only team — no sales floor, no multi-layered account-management hierarchy, no downtown office rent. Every dollar of your retainer pays for operator time on your account, not for someone else to manage the person managing your account.',
      },
      {
        q: 'Are you the cheapest Amazon PPC agency?',
        a: 'No, and we are deliberate about that. There are agencies charging $200–$400/month that will technically touch your account. The economics force them to task-list the work and run high operator-to-brand ratios. If budget is your primary filter, we are probably too expensive. If margin is, we are a fit.',
      },
      {
        q: 'What tech stack do you use? Is it the same as the big agencies?',
        a: 'Yes — Helium 10 for keyword research and listing diagnostics, Data Dive for demand analysis and keyword prioritization, and Adbrew for rule-based bid optimization and dayparting. All three are industry-standard tools used by top-tier North American Amazon agencies. The difference is who is operating them, not what the tools are.',
      },
      {
        q: 'Do you only work with US and Canada based brands?',
        a: 'Primarily yes. US and Canada are our two core markets. We also work with select UK and India based brands, but our content, timezone discipline, and marketplace expertise are built around amazon.com and amazon.ca.',
      },
      {
        q: 'What is the minimum Amazon revenue I need to work with you?',
        a: 'We work best with brands doing at least $50k/month in Amazon revenue. Below that threshold, the retainer-to-revenue ratio stops making sense for the brand. We would rather turn away a bad fit than onboard a client we cannot genuinely move the needle for.',
      },
      {
        q: 'Do you charge a percentage of ad spend on top of the retainer?',
        a: 'No. The $799/month is the entire fee. No percentage of spend, no revenue share, no setup fee, no onboarding fee. Percentage-of-spend fee structures create the wrong incentive — they reward the agency for spending more of your money, not for spending it better. We align with margin, not spend.',
      },
    ],
    get jsonLd() {
      return graph(this.seo.title, this.path, this.seo.description, [
        makeServiceLd(
          'Amazon PPC Agency for North American Brands',
          'Senior-led Amazon PPC agency for US and Canada based brands. Flat $799/month retainer. Same tech stack as $3,000+/month North American agencies — without percentage-of-spend fees.'
        ),
        makeFaqLd(this.faqs),
      ]);
    },
  },

  amazonConsultingServices: {
    path: '/amazon-consulting-services',
    seo: {
      title: 'Amazon Consulting Services | Audits, PPC, Listings, DSP — First Page Consultants',
      description:
        'Amazon consulting services for growth-focused brands. Audits, PPC, account management, listing optimization, DSP and AMC. Senior operators only. Book a free call.',
      keywords:
        'amazon consulting services, amazon consultant, amazon growth consulting, amazon marketplace consulting, seller central consultant, amazon consulting agency',
      hreflang: [
        { lang: 'en-US', href: '/amazon-consulting-services' },
        { lang: 'en-CA', href: '/amazon-consulting-services' },
        { lang: 'x-default', href: '/amazon-consulting-services' },
      ],
    },
    hero: {
      eyebrow: 'Amazon Consulting',
      h1: 'Amazon consulting services for brands that treat Amazon as a real channel',
      sub: 'Audits, PPC management, account management, listing optimization, DSP and AMC — from senior Amazon operators. One team, one owner, no junior handoffs.',
      trust: '10+ years operating inside Amazon. Trusted by DTC brands, aggregators, and VC-backed portfolios.',
    },
    bodyHeadline: 'What a real Amazon consulting engagement looks like',
    bodyIntro:
      'Consulting only works when it produces decisions, not decks. Our Amazon consulting engagements start with a directional audit, translate into a written 90-day roadmap, and then get executed by the same operator who wrote the audit. No handoff between "strategy" and "execution".',
    bodyPoints: [
      {
        h: 'Directional audits, not vanity audits',
        p: 'A 60-minute directional audit covers PPC waste, listing conversion bottlenecks, account health flags, and the two-to-three moves that will actually move revenue this quarter.',
      },
      {
        h: '90-day roadmaps with owners and dates',
        p: 'Every recommendation gets an owner, a date, and a specific expected outcome. No "we recommend improving X" without a plan attached.',
      },
      {
        h: 'PPC + account management + listing, one team',
        p: 'You do not have to hire three vendors. The same operator who plans your PPC restructure also fixes the listing that is bottlenecking conversion.',
      },
      {
        h: 'DSP and AMC when — and only when — it makes sense',
        p: 'We do not push DSP for the sake of it. If your brand does not have the search-side foundation, we fix that first. If it does, DSP and AMC unlock incremental growth cleanly.',
      },
      {
        h: 'Straight talk on when to walk away',
        p: 'If we do not think we can help — because the brand is too early, the fit is wrong, or the ask is fantasy — we will say so on the first call. No obligation.',
      },
    ],
    faqs: [
      {
        q: 'What does an Amazon consultant actually do?',
        a: 'A real Amazon consultant runs directional audits, writes execution-ready 90-day roadmaps, and — in our case — executes those roadmaps hands-on across PPC, account management, and listing optimization.',
      },
      {
        q: 'How much does Amazon consulting cost?',
        a: 'One-off audits and 90-day roadmap engagements are quoted per-project. Ongoing consulting typically starts at our $500/month Account & Listing Optimization retainer or $799/month PPC Management retainer, both with no upsells.',
      },
      {
        q: 'Do you offer Amazon DSP and AMC consulting?',
        a: 'Yes — but only when the search-side foundation is in place. DSP layered on top of a broken PPC account amplifies waste. We audit the foundation first, then recommend DSP if the fit is right.',
      },
      {
        q: 'Do you work with startups or only established brands?',
        a: 'We work best with brands doing at least ~$50k/month in Amazon revenue. Below that, we can still help with directional audits and roadmaps, but full-service retainer work is usually premature.',
      },
    ],
    get jsonLd() {
      return graph(this.seo.title, this.path, this.seo.description, [
        makeServiceLd(
          'Amazon Consulting Services',
          'Amazon consulting for growth-focused brands: audits, PPC, account management, listing optimization, DSP and AMC.'
        ),
        makeFaqLd(this.faqs),
      ]);
    },
  },
};

export const LANDING_PAGE_LIST = Object.values(LANDING_PAGES);
